// ---------------------------------------------------------------------------
// Portfolio Code Quality types
//
// Contract surface for the four /api/portfolio/code-quality-* endpoints that
// power the Code Quality tab on /metric-view. Source of truth shared across
// backend (services, repositories, controllers) and frontend (api wrapper,
// components, tests).
//
// Spec: specs/metric-view-code-quality/
// Epic: PRODOPSAI-328
// ---------------------------------------------------------------------------

/** SonarSource A–E rating (categorical). Stored numerically 1..5 in Sonar's
 *  raw data (1 = A best, 5 = E worst); we use the letter form on the wire. */
export type SonarGrade = 'A' | 'B' | 'C' | 'D' | 'E';

/** Distribution of repos by SonarGrade across an in-scope set. The `nullCount`
 *  bucket captures repos with no rating value (e.g. analysed but no relevant
 *  signal yet) — surfaced as a muted chip in the verdict-strip mix tile. */
export interface IPortfolioCodeQualityGradeMix {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  nullCount: number;
}

/** One bucket on a trend sparkline. `value` is metric-specific:
 *  - coverage axis  → median coverage_pct across in-scope repos in this week
 *  - rating axes    → percentage of in-scope repos at grade A in this week */
export interface IPortfolioCodeQualityTrendBucket {
  bucketStart: string; // ISO week start (Mon, UTC date string)
  value: number | null;
}

/** Quality-level band for a trend card's headline chip. Maps to design-system
 *  semantic tokens (success / primary / warning / error). */
export type CodeQualityLevel = 'high' | 'medium' | 'low' | 'critical';

/** Trend data per axis. Empty axes are omitted from the response array (per US4),
 *  not returned with empty buckets — frontend re-flows the column. */
export interface IPortfolioCodeQualityTrendAxis {
  key: 'coverage' | 'reliability' | 'maintainability';
  metricKey: string; // 'cq_coverage' | 'cq_reliability_rating' | 'cq_maintainability_rating' — matches metric_definitions table (US7)
  buckets: IPortfolioCodeQualityTrendBucket[];
  aggregate: number | null;
  priorAggregate: number | null;
  level: CodeQualityLevel | null;
}

/** Caveat-banner counters from the latest successful FABRIC_SONARQUBE_SYNC run.
 *  When no successful run exists yet, the entire `caveatCounters` field is null
 *  on the wire and the frontend renders the "status unavailable" copy. */
export interface ICaveatCounters {
  matched: number;
  unmatched: number;
  ambiguous: number;
}

/** Per-repo snapshot row used by the distribution column (US5). Carries the
 *  fields needed for ranking + display + drill-through (organization +
 *  project_key for SonarCloud URLs; repository_id for /repositories/:id link). */
export interface IRepoQualityRow {
  repositoryId: string;
  repositoryName: string;
  projectKey: string;
  organization: string | null;
  qualityStatus: 'PASSED' | 'FAILED' | null;
  coveragePct: number | null;
  reliabilityGrade: SonarGrade | null;
  maintainabilityGrade: SonarGrade | null;
  lastAnalysedAt: string | null; // ISO timestamp; null when never analysed
}

/** Per-team aggregate row used by the team-breakouts panels (US6). */
export interface ITeamQualityRow {
  teamId: string;
  teamName: string;
  repoCount: number;
  weightedCoverage: number | null; // weighted-average over team's non-null repos
  qgPassed: number;
  qgTotal: number;
}

/** Aggregate snapshot used by the verdict strip (US3). The `priorAggregate`
 *  shape mirrors `aggregate` so the frontend can compute deltas uniformly. */
export interface IPortfolioCodeQualityAggregate {
  qgPassed: number;
  qgTotal: number;
  medianCoverage: number | null;
  reliabilityGradeMix: IPortfolioCodeQualityGradeMix;
  maintainabilityGradeMix: IPortfolioCodeQualityGradeMix;
}

/** Top-level Code Quality summary response. Shell-level read at page mount. */
export interface IPortfolioCodeQualitySummary {
  aggregate: IPortfolioCodeQualityAggregate;
  priorAggregate: IPortfolioCodeQualityAggregate | null;
  inScopeRepos: number;
  /** Max(last_analysis_at) across in-scope repos — null when no analysis. */
  freshness: string | null;
  /** Live sync-state counters; null when no successful FABRIC_SONARQUBE_SYNC has run. */
  caveatCounters: ICaveatCounters | null;
}

/** Trend response — up to three axes. Axes with absent source metric_key are
 *  omitted (per US1's inventory result and US4 AC). */
export interface IPortfolioCodeQualityTrend {
  axes: IPortfolioCodeQualityTrendAxis[];
}

/** Distribution response — three pre-sorted lists; frontend slices to top 10. */
export interface IPortfolioCodeQualityDistribution {
  failingQg: IRepoQualityRow[];
  lowestCoverage: IRepoQualityRow[];
  worstReliability: IRepoQualityRow[];
}

/** Team-breakouts response. Returns `{ teams: [] }` when filter.teamId is set
 *  (defence in depth — frontend also hides the slot at single-team scope). */
export interface IPortfolioCodeQualityByTeam {
  teams: ITeamQualityRow[];
}

/** Builder for an empty grade-mix — used by stub responses in US2 and as a
 *  zero-value fallback throughout the page when scope has zero in-scope repos. */
export const EMPTY_GRADE_MIX: IPortfolioCodeQualityGradeMix = {
  a: 0, b: 0, c: 0, d: 0, e: 0, nullCount: 0,
} as const;

/** Empty aggregate — used for both `aggregate` and `priorAggregate` in stubs
 *  and when `inScopeRepos === 0`. Never spread; replace wholesale. */
export const EMPTY_CODE_QUALITY_AGGREGATE: IPortfolioCodeQualityAggregate = {
  qgPassed: 0,
  qgTotal: 0,
  medianCoverage: null,
  reliabilityGradeMix: EMPTY_GRADE_MIX,
  maintainabilityGradeMix: EMPTY_GRADE_MIX,
} as const;
